import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { filledSheets, isEmpty, readWorkbook, type SheetInfo } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

/**
 * Größter zusammenhängender Datenblock eines Blattes.
 *
 * Gesucht ist der Bereich, in dem die Daten tatsächlich stehen – nicht der
 * benutzte Bereich, denn der schließt auch eine einzelne Notiz in Zelle H30
 * mit ein. Gezählt werden deshalb die Zeilen, die durchgehend gefüllt sind.
 */
function datenblock(blatt: SheetInfo): { zeilen: number; spalten: number } {
  const gefuellt = blatt.cells.filter((zelle) => !isEmpty(zelle));
  if (gefuellt.length === 0) return { zeilen: 0, spalten: 0 };

  const zeilen = new Set(gefuellt.map((zelle) => zelle.row));
  const spalten = new Set(gefuellt.map((zelle) => zelle.column));
  return { zeilen: zeilen.size, spalten: spalten.size };
}

export const lektionBereiche: Lesson = {
  id: "bereiche",
  title: "Zellen, Bezüge und Bereiche",
  summary: "Die A1-Schreibweise – die Sprache, in der Excel über sich selbst spricht.",
  durationMinutes: 10,
  learningGoals: [
    "Zellen und Bereiche in der A1-Schreibweise benennen",
    "Einen zusammenhängenden Datenbereich mit Kopfzeile anlegen",
    "Sich mit Tastenkürzeln durch große Bereiche bewegen",
  ],
  steps: [
    {
      id: "a1-schreibweise",
      title: "Die A1-Schreibweise",
      explanation: [
        "Jede Zelle hat eine Adresse aus Spaltenbuchstabe und Zeilennummer: B3 ist die " +
          "Zelle in Spalte B, Zeile 3. Ein Bereich wird durch einen Doppelpunkt " +
          "beschrieben – B3:D9 meint das Rechteck von B3 bis D9, also 21 Zellen. " +
          "Diese Schreibweise ist die Sprache, in der jede Formel, jedes Diagramm und " +
          "jede Fehlermeldung mit dir spricht.",
        "Zwei Kurzformen sparen dabei viel Tipparbeit: B:B meint die gesamte Spalte B, " +
          "3:3 die gesamte Zeile 3. Ein Blattname davor mit Ausrufezeichen macht den " +
          "Bezug blattübergreifend: Umsatz!B3:D9. Steht ein Leerzeichen im Blattnamen, " +
          "setzt Excel Hochkommas darum – ein Grund mehr für kurze Blattnamen.",
        "Links neben der Bearbeitungsleiste liegt das Namenfeld. Es zeigt die Adresse " +
          "der markierten Zelle an, und es nimmt auch Eingaben entgegen: Tippst du dort " +
          "B3:D9 ein und drückst die Eingabetaste, springt Excel dorthin und markiert " +
          "den Bereich. Bei großen Tabellen ist das der schnellste Weg von A nach B.",
      ],
      keyPoints: [
        "B3 = Spalte B, Zeile 3. B3:D9 = das Rechteck dazwischen.",
        "B:B ist die ganze Spalte, 3:3 die ganze Zeile.",
        "Das Namenfeld links oben springt zu jeder eingetippten Adresse.",
      ],
    },
    {
      id: "daten-eintragen",
      title: "Einen Datenbereich anlegen",
      explanation: [
        "Daten in Excel gehören in eine Form, die die Software auch versteht: eine " +
          "Kopfzeile mit Spaltenüberschriften, darunter Zeile für Zeile je ein " +
          "Datensatz. Keine Leerzeilen zwischendrin, keine Zwischenüberschriften, keine " +
          "verbundenen Zellen. Diese Form heißt in der Fachsprache „Liste“, und alles " +
          "Weitere in diesem Kurs – Sortieren, Filtern, Diagramme, PivotTables – setzt " +
          "sie voraus.",
        "Die Kopfzeile ist dabei kein Schmuck. Excel liest sie beim Sortieren, beim " +
          "Filtern und beim Erzeugen eines Diagramms aus und benutzt sie als " +
          "Beschriftung. Fehlt sie, rutscht die erste Datenzeile in ihre Rolle – und " +
          "wird beim Sortieren nicht mitsortiert.",
        "Lege jetzt eine solche Liste an. Ein Beispiel, das durch den ganzen Kurs " +
          "trägt: Datum, Region, Produkt, Menge, Preis. Fünf bis zehn Zeilen reichen " +
          "völlig – es geht um die Form, nicht um die Menge.",
      ],
      keyPoints: [
        "Kopfzeile oben, darunter eine Zeile je Datensatz, keine Lücken.",
        "Tabulator springt nach rechts, Eingabetaste in die nächste Zeile zurück.",
        "Strg + Eingabe füllt eine ganze Markierung mit demselben Wert.",
      ],
      task: {
        instruction:
          "Lege auf einem Blatt eine Liste an: eine Kopfzeile mit mindestens drei " +
          "Spaltenüberschriften und darunter mindestens vier Datenzeilen.",
        hint: "Zum Beispiel Datum, Region, Produkt, Menge, Preis – und darunter ein paar Zeilen mit Werten.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Inhalt der Zellen",
              "1.4",
              "Sieh selbst nach, ob deine Liste eine Kopfzeile und darunter mindestens vier gefüllte Zeilen hat.",
            );
          }

          const blaetter = filledSheets(mappe);
          if (blaetter.length === 0) {
            return fail(KEIN_INHALT, "Schreibe zuerst eine Kopfzeile und ein paar Datenzeilen darunter.");
          }

          const beste = blaetter
            .map((blatt) => ({ blatt, ...datenblock(blatt) }))
            .sort((a, b) => b.zeilen * b.spalten - a.zeilen * a.spalten)[0];

          return fromCriteria(
            [
              criterion(
                `Mindestens drei Spalten gefüllt (gefunden auf „${beste.blatt.name}“: ${beste.spalten})`,
                beste.spalten >= 3,
              ),
              criterion(
                `Kopfzeile plus mindestens vier Datenzeilen, also fünf Zeilen (gefunden: ${beste.zeilen})`,
                beste.zeilen >= 5,
              ),
            ],
            {
              success:
                `Auf „${beste.blatt.name}“ steht eine Liste mit ${beste.spalten} Spalten ` +
                `und ${beste.zeilen} Zeilen – genau die Form, mit der Excel arbeiten kann.`,
              failure: "Die Liste ist noch nicht vollständig.",
              hint: "Achte darauf, dass zwischen den Zeilen keine Leerzeile steht – Excel würde die Liste dort enden lassen.",
            },
          );
        },
      },
    },
    {
      id: "navigieren",
      title: "Sich in großen Blättern bewegen",
      explanation: [
        "Ein Tabellenblatt hat über eine Million Zeilen. Mit dem Mausrad kommt man da " +
          "nicht weit, und die Bildlaufleiste wird bei großen Datenmengen zur " +
          "Millimeterarbeit. Die Tastatur ist hier deutlich schneller.",
        "Strg + Pfeiltaste springt in die angegebene Richtung bis zum Ende des " +
          "zusammenhängenden Bereichs. Steht der Cursor in einer gefüllten Zelle, " +
          "landet er auf der letzten gefüllten Zelle vor der nächsten Lücke – das ist " +
          "gleichzeitig der schnellste Test, ob eine Liste wirklich lückenlos ist. " +
          "Strg + Umschalt + Pfeiltaste macht dasselbe und markiert dabei.",
        "Strg + Pos1 springt immer zurück nach A1, Strg + Ende zur letzten benutzten " +
          "Zelle des Blattes. Der zweite Kürzel ist auch diagnostisch nützlich: Landest " +
          "du weit außerhalb deiner Daten, steht dort noch etwas – oft ein " +
          "Leerzeichen oder eine übrig gebliebene Formatierung, die die Datei " +
          "unnötig aufbläht.",
      ],
      keyPoints: [
        "Strg + Pfeiltaste springt ans Ende des zusammenhängenden Bereichs.",
        "Strg + Umschalt + Pfeiltaste markiert bis dorthin.",
        "Strg + Pos1 geht zu A1, Strg + Ende zur letzten benutzten Zelle.",
      ],
      task: {
        instruction:
          "Prüfe mit Strg + Ende, ob deine Arbeitsmappe Reste außerhalb der eigentlichen " +
          "Daten enthält. Lösche solche Zellen, sodass der benutzte Bereich jedes Blattes " +
          "bei deinen Daten endet.",
        hint: "Landest du mit Strg + Ende weit rechts oder unten: Zeilen bzw. Spalten dazwischen markieren, per Rechtsklick löschen, speichern.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der benutzte Bereich eines Blattes",
              "1.4",
              "Drücke auf jedem Blatt Strg + Ende und sieh nach, ob du bei deinen Daten landest.",
            );
          }

          const blaetter = filledSheets(mappe);
          if (blaetter.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst im vorigen Schritt deine Liste ein.");
          }

          // Ein benutzter Bereich, der deutlich mehr Zellen umfasst als tatsächlich
          // gefüllt sind, deutet auf Reste außerhalb der Daten hin.
          const auffaellig = blaetter.filter((blatt) => {
            const gefuellt = blatt.cells.filter((zelle) => !isEmpty(zelle)).length;
            return gefuellt > 0 && blatt.cells.length > gefuellt * 3 && blatt.cells.length - gefuellt > 40;
          });

          if (auffaellig.length === 0) {
            return pass(
              "Der benutzte Bereich endet auf jedem Blatt dort, wo auch die Daten enden – keine Reste.",
            );
          }

          const erstes = auffaellig[0];
          const gefuellt = erstes.cells.filter((zelle) => !isEmpty(zelle)).length;
          return fail(
            `Auf „${erstes.name}“ reicht der benutzte Bereich bis ${erstes.usedRangeAddress ?? "unbekannt"}, ` +
              `enthält aber nur ${gefuellt} gefüllte Zellen. Da steht noch etwas außerhalb deiner Daten.`,
            "Springe mit Strg + Ende dorthin. Markiere die leeren Zeilen und Spalten zwischen deinen Daten " +
              "und dieser Stelle, lösche sie per Rechtsklick und speichere – erst dann rechnet Excel den " +
              "benutzten Bereich neu.",
          );
        },
      },
    },
  ],
};
